









































// vue
import {
    defineComponent,
    onMounted,
    onUnmounted,
    ref,
} from '@vue/composition-api';

// models
import { MeasureRecord, ActiveMeasureRecordTool } from '@/vue3/api/models';

// services
import { useService } from '@/vue3/api/services';

// hooks
import { useStore } from '@/store';
import { useMeasureRecord } from '@/vue3/hooks/measureRecordHook';
import { useGeoRecord } from '@/vue3/hooks/geoRecordHook';
import { useGeometryHoverHook } from '@/vue3/hooks/geometryHoverHook';
import { useNotify } from '@/vue3/hooks/notifyHook';
import { useCursor } from '@/vue3/hooks/cursorHook';

// primevue
import Button from 'primevue/button';
import Panel from 'primevue/panel';

// config
import Tools from '@/vue3/configs/measureToolsConfig';
import measureRecordLayers from '@/vue3/configs/measureRecordLayers';

// mapbox
import MeasureLineStringMode from '@/vue3/mapbox/measureLineStringMode';
import MeasurePolygonMode from '@/vue3/mapbox/measurePolygonMode';

export default defineComponent({
    components: {
        Button,
        Panel,
    },
    props: {
        mapbox: {
            type: Object,
            default: () => {},
        },
    },
    setup(props: any) {
        // services
        const { createMeasureRecord, deleteMeasureRecord } = useService();

        // hooks
        const store = useStore();
        const { addMeasureRecord, removeMeasureRecord } = useMeasureRecord();
        const { initCursorSource, removeCursorSource } = useGeoRecord();
        const { highlightGeometry } = useGeometryHoverHook();
        const { warnToast } = useNotify();
        const { handleMoveCursor } = useCursor();

        // models
        let measureRecord: MeasureRecord = {} as MeasureRecord;

        // refs
        const activeTool = ref<ActiveMeasureRecordTool>({
            activeToolName: '',
            activeToolDesc: '',
            activeToolMode: '',
        });

        const isTouchDevice = ref<boolean>(false);

        // methods
        const stopMeasuring = async () => {
            measureRecord.features = [];

            let tempFeatures =
                activeTool.value.activeToolName === 'Line Measure Tool'
                    ? MeasureLineStringMode.onDrawFinished()
                    : MeasurePolygonMode.onDrawFinished();

            let collection =
                activeTool.value.activeToolName === 'Line Measure Tool'
                    ? tempFeatures.collection.features
                    : tempFeatures.polygon;

            if (activeTool.value.activeToolName === 'Line Measure Tool') {
                measureRecord.features = collection;

                removeFirstElementFromFeatures();

                let measurementTotal = measureRecord.features.reduce(
                    (acc, feature) => {
                        return acc + feature.properties.measurement;
                    },
                    0,
                );

                measureRecord.features.forEach((feature) => {
                    feature.properties.measurementTotal = measurementTotal;
                });
            } else {
                collection.coordinates[0].pop();
                collection.coordinates[0].push(collection.coordinates[0][0]);

                let tempFeature = {
                    id: 1,
                    type: 'Feature',
                    properties: {
                        active: 'true',
                        measurement: collection.properties.measurement,
                        measurementLabel:
                            collection.properties.measurementLabel,
                        measurementTotal:
                            collection.properties.measurementTotal,
                        isVisible: true,
                        measureRecordId: 1,
                    },
                    geometry: {
                        coordinates: collection.coordinates,
                        type: 'Polygon',
                    },
                };

                measureRecord.features.push(tempFeature);
            }

            props.mapbox.draw.deleteAll();
            props.mapbox.draw.changeMode(activeTool.value.activeToolMode);

            try {
                await addMeasureRecordToDatabase(measureRecord);
            } catch (error) {
                console.log(error)
                toolClick(activeTool.value);
                return;
            }

            addMeasureRecord(
                props.mapbox.map,
                measureRecord,
                store.state.isMeters,
            );

            toolClick(
                Tools.find(
                    (tool) => tool.name === activeTool.value.activeToolName,
                ),
            );
        };
        const moveCursor = ({ lngLat }): void => {
            handleMoveCursor(props.mapbox, false, '', lngLat);
        };

        const activateCursor = () => {
            if (props.mapbox.map.getSource('create-cursor-point')) return;

            initCursorSource(props.mapbox.map);
            props.mapbox.map.on('mousemove', moveCursor);
        };

        const onMeasureMouseMove = (e: any): void => {
            if (activeTool.value.activeToolName !== 'Measure Eraser Tool')
                return;

            highlightGeometry(e, props.mapbox.map, 'measure-records');
        };

        const activateMouseMove = (): void => {
            measureRecordLayers.forEach(({ id }) => {
                props.mapbox.map.on('mousemove', id, onMeasureMouseMove);
                props.mapbox.map.on('mouseleave', id, onMeasureMouseMove);
            });
        };

        const deActivateMouseMove = (): void => {
            measureRecordLayers.forEach(({ id }) => {
                props.mapbox.map.off('mousemove', id, onMeasureMouseMove);
                props.mapbox.map.off('mouseleave', id, onMeasureMouseMove);
            });
        };

        const onDeleteEdgeCase = (sourceType) => {
            if (sourceType !== 'geo-records') return;

            warnToast(
                'Cannot erase Geo Record with this tool, please select the Geo Record with the Select Tool.',
            );
        };

        const onClickDeleteMeasureRecord = async (e: any): Promise<void> => {
            const features = props.mapbox.map.queryRenderedFeatures(e.point);

            if (!features[0]) return;

            const sourceType = features[0].layer.source;

            onDeleteEdgeCase(sourceType);

            if (sourceType !== 'measure-records') return;

            const measureId = features[0].properties.measureRecordId;

            await deleteMeasureRecord(measureId);
            removeMeasureRecord(props.mapbox.map, measureId);
        };

        const addMeasureRecordToDatabase = async (
            newMeasureRecord: MeasureRecord,
        ): Promise<void> => {
            let features: any = {};
            let temp: any = [];

            newMeasureRecord.features.forEach((feature, i) => {
                if (feature.properties.measurement === 0) return;

                // features[i] = feature;

                temp.push(feature);
            });

            temp.forEach((feature, i) => {
                features[i] = feature;
            });

            measureRecord.id = (await createMeasureRecord(features)).id;

            props.mapbox.draw.deleteAll();
        };

        const removeFirstElementFromFeatures = (): void => {
            measureRecord.features.shift();

            const label =
                measureRecord.features[0].properties.totalMeasurementLabel;

            if (label !== 'origin') return;

            measureRecord.features.shift();
        };

        const drawMeasureRecord = async (): Promise<void> => {
            measureRecord.features = props.mapbox.draw.getAll().features;

            if (activeTool.value.activeToolName === 'Line Measure Tool')
                removeFirstElementFromFeatures();

            try {
                await addMeasureRecordToDatabase(measureRecord);
            } catch (error) {
                console.log(error)
                toolClick(activeTool.value);
                return;
            }

            addMeasureRecord(
                props.mapbox.map,
                measureRecord,
                store.state.isMeters,
            );

            toolClick(
                Tools.find(
                    (tool) => tool.name === activeTool.value.activeToolName,
                ),
            );
        };

        const toggleMeausureDrawMode = (isMeasureToolActive: boolean): void => {
            isMeasureToolActive
                ? (props.mapbox.map.off('click', onClickDeleteMeasureRecord),
                  props.mapbox.map.on('draw.create', drawMeasureRecord))
                : (props.mapbox.map.on('click', onClickDeleteMeasureRecord),
                  props.mapbox.map.off('draw.create', drawMeasureRecord));

            if (window.ontouchstart !== undefined && isMeasureToolActive) {
                props.mapbox.map.off('touchstart', onClickDeleteMeasureRecord);
            } else {
                props.mapbox.map.on('touchstart', onClickDeleteMeasureRecord);
            }
        };

        const measureRecordEraser = (): void => {
            activateMouseMove();
            removeCursorSource(props.mapbox.map);
            toggleMeausureDrawMode(false);
        };

        const measureRecordCreator = (): void => {
            deActivateMouseMove();
            toggleMeausureDrawMode(true);

            if (props.mapbox.map.getSource('create-cursor-point')) return;

            activateCursor();
        };

        const toolClick = (tool: any): void => {
            props.mapbox.draw.changeMode(tool.mode);

            activeTool.value = {
                activeToolName: tool.name,
                activeToolDesc: tool.description,
                activeToolMode: tool.mode,
            };

            activeTool.value.activeToolName === 'Measure Eraser Tool'
                ? measureRecordEraser()
                : measureRecordCreator();
        };

        const unmountTool = (): void => {
            removeCursorSource(props.mapbox.map);
            deActivateMouseMove();

            props.mapbox.draw.deleteAll();
            props.mapbox.map.off('click', onClickDeleteMeasureRecord);
            props.mapbox.map.off('draw.create', drawMeasureRecord);
            props.mapbox.map.off('touchstart', onClickDeleteMeasureRecord);
        };

        const checkTouchDevice = () => {
            if (window.ontouchstart !== undefined) isTouchDevice.value = true;
        };

        // lifecycle
        onMounted(() => {
            checkTouchDevice();
            toolClick(Tools[0]);
        });

        onUnmounted(() => {
            unmountTool();
        });

        return {
            toolClick,
            Tools,
            activeTool,
            isTouchDevice,
            stopMeasuring,
        };
    },
});
